import { useState, useCallback } from 'react';
import * as Crypto from 'expo-crypto';

const DEFAULT_DESIGN = {
    globalStyles: {
        backgroundColor: '#f6f9fc',
        contentWidth: 600,
        fontFamily: 'Arial, Helvetica, sans-serif',
    },
    rows: [],
};

const DEFAULT_ROW_STYLES = {
    backgroundColor: '#ffffff',
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
};

const DEFAULT_BLOCK_STYLES = {
    paddingTop: 10,
    paddingRight: 20,
    paddingBottom: 10,
    paddingLeft: 20,
    backgroundColor: '#ffffff',
    textAlign: 'left',
};

const generateId = () => Crypto.randomUUID();

const normalizeDesign = (design) => ({
    ...DEFAULT_DESIGN,
    ...(design || {}),
    globalStyles: { ...DEFAULT_DESIGN.globalStyles, ...(design?.globalStyles || {}) },
    rows: Array.isArray(design?.rows) ? design.rows : [],
});

const useEmailEditor = (initialDesign = null) => {
    const [design, setDesign] = useState(() => normalizeDesign(initialDesign));
    const [selectedId, setSelectedId] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);

    const saveToHistory = useCallback((newDesign) => {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(JSON.parse(JSON.stringify(newDesign)));
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    }, [history, historyIndex]);

    const addRow = useCallback((afterRowId = null) => {
        setDesign((prev) => {
            const newRow = {
                id: generateId(),
                columns: [
                    {
                        id: generateId(),
                        width: 100,
                        blocks: [],
                    },
                ],
                styles: { ...DEFAULT_ROW_STYLES },
            };

            let newRows;
            if (afterRowId) {
                const index = prev.rows.findIndex(r => r.id === afterRowId);
                newRows = [
                    ...prev.rows.slice(0, index + 1),
                    newRow,
                    ...prev.rows.slice(index + 1),
                ];
            } else {
                newRows = [...prev.rows, newRow];
            }

            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const removeRow = useCallback((rowId) => {
        setDesign((prev) => {
            const newRows = prev.rows.filter(r => r.id !== rowId);
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
        if (selectedId === rowId) setSelectedId(null);
    }, [selectedId, saveToHistory]);

    const moveRow = useCallback((rowId, direction) => {
        setDesign((prev) => {
            const rowIndex = prev.rows.findIndex(r => r.id === rowId);
            if (rowIndex === -1) return prev;

            const newIndex = direction === 'up' ? rowIndex - 1 : rowIndex + 1;
            if (newIndex < 0 || newIndex >= prev.rows.length) return prev;

            const newRows = [...prev.rows];
            const [movedRow] = newRows.splice(rowIndex, 1);
            newRows.splice(newIndex, 0, movedRow);

            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const addColumn = useCallback((rowId) => {
        setDesign((prev) => {
            const newRows = prev.rows.map(row => {
                if (row.id === rowId) {
                    const currentColumns = row.columns;
                    const newColumnCount = Math.min(currentColumns.length + 1, 3);
                    const newWidth = 100 / newColumnCount;
                    return {
                        ...row,
                        columns: [
                            ...currentColumns.map(col => ({ ...col, width: newWidth })),
                            {
                                id: generateId(),
                                width: newWidth,
                                blocks: [],
                            },
                        ],
                    };
                }
                return row;
            });
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const removeColumn = useCallback((rowId, colId) => {
        setDesign((prev) => {
            const newRows = prev.rows.map(row => {
                if (row.id === rowId && row.columns.length > 1) {
                    const newColumns = row.columns.filter(col => col.id !== colId);
                    const newWidth = 100 / newColumns.length;
                    return {
                        ...row,
                        columns: newColumns.map(col => ({ ...col, width: newWidth })),
                    };
                }
                return row;
            });
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
        if (selectedId === colId) setSelectedId(null);
    }, [selectedId, saveToHistory]);

    const setColumnWidths = useCallback((rowId, widths) => {
        setDesign((prev) => {
            const newRows = prev.rows.map(row => {
                if (row.id === rowId) {
                    return {
                        ...row,
                        columns: row.columns.map((col, i) => ({
                            ...col,
                            width: widths[i] || col.width,
                        })),
                    };
                }
                return row;
            });
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const addBlock = useCallback((colId, blockType, afterBlockId = null) => {
        setDesign((prev) => {
            const newRows = prev.rows.map(row => ({
                ...row,
                columns: row.columns.map(col => {
                    if (col.id === colId) {
                        const newBlock = {
                            id: generateId(),
                            type: blockType,
                            content: getDefaultContentForType(blockType),
                            styles: { ...DEFAULT_BLOCK_STYLES },
                        };

                        let newBlocks;
                        if (afterBlockId) {
                            const index = col.blocks.findIndex(b => b.id === afterBlockId);
                            newBlocks = [
                                ...col.blocks.slice(0, index + 1),
                                newBlock,
                                ...col.blocks.slice(index + 1),
                            ];
                        } else {
                            newBlocks = [...col.blocks, newBlock];
                        }

                        return { ...col, blocks: newBlocks };
                    }
                    return col;
                }),
            }));
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const removeBlock = useCallback((colId, blockId) => {
        setDesign((prev) => {
            const updatedRows = prev.rows.map(row => ({
                ...row,
                columns: row.columns.map(col => {
                    if (col.id === colId) {
                        return {
                            ...col,
                            blocks: col.blocks.filter(b => b.id !== blockId),
                        };
                    }
                    return col;
                }),
            }));
            // Auto-remove rows where every column is empty
            const newRows = updatedRows.filter(row =>
                row.columns.some(col => col.blocks.length > 0)
            );
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
        if (selectedId === blockId) setSelectedId(null);
    }, [selectedId, saveToHistory]);

    const moveBlock = useCallback((fromColId, toColId, blockId, newIndex) => {
        setDesign((prev) => {
            let movedBlock = null;
            const newRows = prev.rows.map(row => ({
                ...row,
                columns: row.columns.map(col => {
                    if (col.id === fromColId) {
                        const blockIndex = col.blocks.findIndex(b => b.id === blockId);
                        if (blockIndex !== -1) {
                            movedBlock = col.blocks[blockIndex];
                            return {
                                ...col,
                                blocks: col.blocks.filter(b => b.id !== blockId),
                            };
                        }
                    }
                    return col;
                }),
            }));

            if (movedBlock) {
                const finalRows = newRows.map(row => ({
                    ...row,
                    columns: row.columns.map(col => {
                        if (col.id === toColId) {
                            const newBlocks = [...col.blocks];
                            newBlocks.splice(newIndex, 0, movedBlock);
                            return { ...col, blocks: newBlocks };
                        }
                        return col;
                    }),
                }));
                const newDesign = { ...prev, rows: finalRows };
                saveToHistory(newDesign);
                return newDesign;
            }
            return prev;
        });
    }, [saveToHistory]);

    const updateBlockContent = useCallback((blockId, content) => {
        setDesign((prev) => {
            const newRows = prev.rows.map(row => ({
                ...row,
                columns: row.columns.map(col => ({
                    ...col,
                    blocks: col.blocks.map(block =>
                        block.id === blockId
                            ? { ...block, content: { ...block.content, ...content } }
                            : block
                    ),
                })),
            }));
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const updateBlockStyles = useCallback((blockId, styles) => {
        setDesign((prev) => {
            const newRows = prev.rows.map(row => ({
                ...row,
                columns: row.columns.map(col => ({
                    ...col,
                    blocks: col.blocks.map(block =>
                        block.id === blockId
                            ? { ...block, styles: { ...block.styles, ...styles } }
                            : block
                    ),
                })),
            }));
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const updateRowStyles = useCallback((rowId, styles) => {
        setDesign((prev) => {
            const newRows = prev.rows.map(row =>
                row.id === rowId
                    ? { ...row, styles: { ...row.styles, ...styles } }
                    : row
            );
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const updateGlobalStyles = useCallback((styles) => {
        setDesign((prev) => {
            const newDesign = {
                ...prev,
                globalStyles: { ...prev.globalStyles, ...styles },
            };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const duplicateBlock = useCallback((blockId) => {
        setDesign((prev) => {
            const newRows = prev.rows.map(row => ({
                ...row,
                columns: row.columns.map(col => {
                    const blockIndex = col.blocks.findIndex(b => b.id === blockId);
                    if (blockIndex !== -1) {
                        const block = col.blocks[blockIndex];
                        const newBlock = {
                            ...JSON.parse(JSON.stringify(block)),
                            id: generateId(),
                        };
                        const newBlocks = [
                            ...col.blocks.slice(0, blockIndex + 1),
                            newBlock,
                            ...col.blocks.slice(blockIndex + 1),
                        ];
                        return { ...col, blocks: newBlocks };
                    }
                    return col;
                }),
            }));
            const newDesign = { ...prev, rows: newRows };
            saveToHistory(newDesign);
            return newDesign;
        });
    }, [saveToHistory]);

    const undo = useCallback(() => {
        if (historyIndex > 0) {
            setHistoryIndex(historyIndex - 1);
            setDesign(history[historyIndex - 1]);
        }
    }, [history, historyIndex]);

    const redo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            setHistoryIndex(historyIndex + 1);
            setDesign(history[historyIndex + 1]);
        }
    }, [history, historyIndex]);

    const getSelectedBlock = useCallback(() => {
        for (const row of design.rows) {
            for (const col of row.columns) {
                const block = col.blocks.find(b => b.id === selectedId);
                if (block) return block;
            }
        }
        return null;
    }, [design, selectedId]);

    const getSelectedRow = useCallback(() => {
        for (const row of design.rows) {
            if (row.id === selectedId) return row;
            for (const col of row.columns) {
                if (col.id === selectedId) return row;
                if (col.blocks.find(b => b.id === selectedId)) return row;
            }
        }
        return null;
    }, [design, selectedId]);

    return {
        design,
        selectedId,
        setSelectedId,
        addRow,
        removeRow,
        moveRow,
        addColumn,
        removeColumn,
        setColumnWidths,
        addBlock,
        removeBlock,
        moveBlock,
        updateBlockContent,
        updateBlockStyles,
        updateRowStyles,
        updateGlobalStyles,
        duplicateBlock,
        undo,
        redo,
        getSelectedBlock,
        getSelectedRow,
        canUndo: historyIndex > 0,
        canRedo: historyIndex < history.length - 1,
    };
};

function getDefaultContentForType(type) {
    switch (type) {
        case 'text':
            return { html: '<p>Enter your text here...</p>' };
        case 'image':
            return { src: '', alt: '', width: '100%', href: '', align: 'center' };
        case 'button':
            return {
                text: 'Click Here',
                href: 'https://',
                backgroundColor: '#1460a5',
                textColor: '#ffffff',
                borderRadius: 4,
                align: 'center',
                fullWidth: false,
            };
        case 'divider':
            return { color: '#e5e7eb', thickness: 1, width: '100%' };
        case 'spacer':
            return { height: 20 };
        case 'html':
            return { rawHtml: '' };
        default:
            return {};
    }
}

export default useEmailEditor;
